import React, { useState } from 'react';
import { Poppins, Spacer } from 'src/common';
import { VariablePill } from 'src/common/VariablePill';
import Button from 'src/components/form/Button';
import CurrencyInput from 'src/components/form/CurrencyInput';
import Input from 'src/components/form/Input';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { MissingVariableEntry, MissingVariableValue } from './util';
import * as S from './MissingVariablesModal.styles';

// Mirrors the BE variables validation ('Enter value higher than 0.001').
const MIN_VARIABLE_VALUE = 0.001;

export type Destination = 'rmp' | 'assessment' | 'scenario' | 'populate';

const COPY: Record<
  Destination,
  {
    single: string;
    plural: string;
    saveLabel: string;
    /** false for flavours with nothing to proceed to (populate = plain value entry). */
    showSkip: boolean;
    skipLabel: string;
    skipMpButton: 'Proceed & do this in RMP' | 'Proceed & add values later';
  }
> = {
  rmp: {
    single:
      'This control uses variables that are unpopulated in your workspace. Add these variables before you proceed to add to your risk management plan.',
    plural:
      'These controls use variables that are unpopulated in your workspace. Add these variables before you proceed to add to your risk management plan.',
    saveLabel: 'SAVE & PROCEED',
    showSkip: true,
    skipLabel: 'PROCEED & DO THIS IN RMP',
    skipMpButton: 'Proceed & do this in RMP',
  },
  assessment: {
    single:
      'This scenario uses variables that are unpopulated in your workspace. Add these variables before you proceed to add to your assessment.',
    plural:
      'These scenarios use variables that are unpopulated in your workspace. Add these variables before you proceed to add to your assessment.',
    saveLabel: 'SAVE & PROCEED',
    showSkip: true,
    skipLabel: 'PROCEED & ADD VALUES LATER',
    skipMpButton: 'Proceed & add values later',
  },
  // Placeholder copy pending Jennifer/Gideon sign-off (CRPD-7209 I002 round).
  scenario: {
    single:
      'This control uses variables that are unpopulated in your workspace. Add these variables before you proceed to link to your scenario.',
    plural:
      'These controls use variables that are unpopulated in your workspace. Add these variables before you proceed to link to your scenario.',
    saveLabel: 'SAVE & PROCEED',
    showSkip: true,
    skipLabel: 'PROCEED & ADD VALUES LATER',
    skipMpButton: 'Proceed & add values later',
  },
  // Missing-pill click on RMP surfaces (nothing is being added — plain value entry).
  // Placeholder copy pending Jennifer/Gideon sign-off.
  populate: {
    single:
      'This control uses variables that are unpopulated in your workspace. Add values to calculate its costs.',
    plural:
      'These controls use variables that are unpopulated in your workspace. Add values to calculate their costs.',
    saveLabel: 'SAVE',
    showSkip: false,
    skipLabel: 'PROCEED & ADD VALUES LATER',
    skipMpButton: 'Proceed & add values later',
  },
};

interface RowState {
  value: number | null;
  currency: boolean;
  touched: boolean;
}

interface MissingVariablesModalProps {
  entries: MissingVariableEntry[];
  destination: Destination;
  plural?: boolean;
  saving?: boolean;
  onSaveAndProceed: (values: MissingVariableValue[]) => void;
  onSkip: () => void;
  onClose: () => void;
}

export const MissingVariablesModal: React.FC<MissingVariablesModalProps> = ({
  entries,
  destination,
  plural,
  saving,
  onSaveAndProceed,
  onSkip,
  onClose,
}) => {
  const [rows, setRows] = useState<Record<string, RowState>>(() =>
    Object.fromEntries(
      entries.map((entry) => [
        entry.token,
        { value: null, currency: entry.variable?.currency ?? true, touched: false },
      ]),
    ),
  );

  const copy = COPY[destination];
  const isRowValid = (row: RowState) => row.value != null && row.value > MIN_VARIABLE_VALUE;
  const allValid = entries.every((entry) => isRowValid(rows[entry.token]));

  const updateRow = (token: string, patch: Partial<RowState>) => {
    setRows((prev) => ({ ...prev, [token]: { ...prev[token], ...patch } }));
  };

  return (
    <S.StyledModal onRequestClose={onClose} isOpen center persistent={saving}>
      <Poppins className="m-title" px={28} data-cy="missing-variables-modal-title">
        Missing Variables
      </Poppins>
      <Spacer $px={12} />
      <Poppins px={14} color="davysGrey">
        {plural ? copy.plural : copy.single}
      </Poppins>
      <Spacer $px={30} />
      <div className="mvm-rows">
        {entries.map((entry) => {
          const row = rows[entry.token];
          return (
            <div className="mvm-row" key={entry.token} data-cy="missing-variable-row">
              <div className="mvm-row__pill">
                <VariablePill unpopulated>
                  <Poppins px={14} weight={500} color="statusRed" nowrap>
                    {entry.displayName}
                  </Poppins>
                </VariablePill>
                {!entry.variable && (
                  <div className="mvm-row__currency">
                    <Poppins px={12} weight={500} color="cflowerBlue" nowrap>
                      CURRENCY
                    </Poppins>
                    <Input
                      checked={row.currency}
                      onChange={(e) => updateRow(entry.token, { currency: e.target.checked })}
                      className="switch"
                      type="checkbox"
                    />
                  </div>
                )}
              </div>
              <CurrencyInput
                placeholder="Enter value"
                value={row.value}
                currency={row.currency}
                onValueChange={({ floatValue }: { floatValue?: number }) =>
                  updateRow(entry.token, { value: floatValue ?? null })
                }
                onBlur={() => updateRow(entry.token, { touched: true })}
                error={row.touched && !isRowValid(row)}
                fullWidth
                data-cy="missing-variable-value-input"
              />
            </div>
          );
        })}
      </div>
      <Spacer $px={40} />
      <div className={`mvm-buttons${copy.showSkip ? '' : ' mvm-buttons--single'}`}>
        {copy.showSkip && (
          <Button
            secondary
            disabled={saving}
            onClick={() => {
              onSkip();
              mpEvent(MPEvents.ButtonClick, {
                button: copy.skipMpButton,
                modal: 'Missing variables modal',
              });
            }}
            data-cy="missing-variables-skip"
          >
            {copy.skipLabel}
          </Button>
        )}
        <Button
          primary
          disabled={!allValid || saving}
          loading={saving}
          onClick={() => {
            onSaveAndProceed(
              entries.map((entry) => ({
                entry,
                value: rows[entry.token].value as number,
                currency: rows[entry.token].currency,
              })),
            );
            mpEvent(MPEvents.ButtonClick, {
              button: 'Save & Proceed',
              modal: 'Missing variables modal',
            });
          }}
          data-cy="missing-variables-save"
        >
          {copy.saveLabel}
        </Button>
      </div>
    </S.StyledModal>
  );
};
